import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createReportingOtp, verifyReportingOtp } from "@/api/reporting";
import useSettings from "@/hooks/useSettings";
import { toast } from "react-hot-toast";

import {
  getRemainingReportingOtpCountdown,
  setReportingOtpExpiry,
  clearReportingOtpExpiry,
  setReportingAccessVerified,
} from "./reporting-otp-storage";
import ReportingChannelStep from "./reporting-channel-step";
import ReportingVerifyStep from "./reporting-verify-step";

export default function ReportingOtpModal({
  isOpen = true,
  designation = "Manager",
  onSuccess,
}) {
  const navigate = useNavigate();
  const { settings } = useSettings();
  const restaurant = settings?.restaurant;

  const [step, setStep] = useState(() => {
    // If there is an active countdown, resume on OTP step
    const remaining = getRemainingReportingOtpCountdown();
    return remaining > 0 ? "otp" : "channel";
  });
  const [channel, setChannel] = useState("sms"); // "sms" | "email"
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(getRemainingReportingOtpCountdown);

  // Sync countdown timer every second
  useEffect(() => {
    if (resendCountdown <= 0) return;

    const timer = setInterval(() => {
      const remaining = getRemainingReportingOtpCountdown();
      if (remaining <= 0) {
        clearReportingOtpExpiry();
        setResendCountdown(0);
      } else {
        setResendCountdown(remaining);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCountdown]);

  const restaurantMobile =
    restaurant?.mobile || restaurant?.phone || "Mobile number not configured";
  const restaurantEmail =
    restaurant?.email || "Email address not configured";

  // Step 1: Send OTP to selected channel
  const handleSendOtp = async (overrideChannel) => {
    if (resendCountdown > 0 || sendingOtp) return;

    const targetChannel =
      typeof overrideChannel === "string" ? overrideChannel : channel;

    setSendingOtp(true);
    setOtpError("");

    try {
      const payload = {
        purpose: "reporting_access",
        channel: targetChannel,
      };

      const res = await createReportingOtp(payload);

      if (
        res?.status === "success" ||
        res?.status === "Success" ||
        res?.message?.toLowerCase()?.includes("success")
      ) {
        toast.success(
          res?.message ||
            `Verification OTP sent to restaurant ${targetChannel === "sms" ? "mobile" : "email"}.`
        );
        setReportingOtpExpiry(60);
        setResendCountdown(60);
        setStep("otp");
      } else {
        const errorMsg =
          res?.message ||
          "Failed to send OTP. Please verify your contact information.";
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error("Error sending reporting OTP:", err);
      toast.error(err?.message || "Something went wrong while sending OTP.");
    } finally {
      setSendingOtp(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (codeToVerify) => {
    const code = codeToVerify || otpCode;
    if (!code || !code.trim()) {
      setOtpError("Please enter the OTP code.");
      return;
    }

    setVerifying(true);
    setOtpError("");

    try {
      const payload = {
        purpose: "reporting_access",
        otp_code: code.trim(),
      };

      const res = await verifyReportingOtp(payload);

      const isSuccess =
        res?.status === "success" ||
        res?.status === "Success" ||
        res?.message?.toLowerCase()?.includes("success");

      if (isSuccess) {
        toast.success(res?.message || "Reporting access authorized successfully.");
        clearReportingOtpExpiry();
        setReportingAccessVerified();
        setResendCountdown(0);
        setOtpError("");
        if (typeof onSuccess === "function") {
          onSuccess();
        }
      } else {
        // Failed / Invalid OTP: MUST NOT CLOSE! Show backend message
        const errorMsg =
          res?.message ||
          "OTP Is Invalid Or Has Expired. Please try again.";
        setOtpError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Verification failed. Please check your OTP code.";
      setOtpError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setVerifying(false);
    }
  };

  const handleExit = () => {
    navigate("/home");
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-md transition-all duration-300 animate-in fade-in-0"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-[460px] bg-[#141414]/95 backdrop-blur-xl border border-neutral-800 text-white p-6 shadow-2xl rounded-2xl animate-in fade-in-0 zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {step === "channel" ? (
          <ReportingChannelStep
            channel={channel}
            setChannel={setChannel}
            restaurantMobile={restaurantMobile}
            restaurantEmail={restaurantEmail}
            resendCountdown={resendCountdown}
            sendingOtp={sendingOtp}
            designation={designation}
            onSendOtp={() => handleSendOtp()}
            onGoToOtp={() => setStep("otp")}
            onExit={handleExit}
          />
        ) : (
          <ReportingVerifyStep
            channel={channel}
            otpCode={otpCode}
            setOtpCode={setOtpCode}
            otpError={otpError}
            setOtpError={setOtpError}
            resendCountdown={resendCountdown}
            sendingOtp={sendingOtp}
            verifying={verifying}
            onSendOtp={() => handleSendOtp()}
            onVerifyOtp={handleVerifyOtp}
            onBackToChannel={() => setStep("channel")}
            onExit={handleExit}
          />
        )}
      </div>
    </div>
  );
}
